import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class CsvExportService {

  constructor() {}

  exportToCsv<T extends object>(data: T[] | null | undefined, filename: string = 'export.csv', headers: string[]): void {

    if ((!data || !data.length) && headers.length) {
      this.downloadBlob(headers.join(','), filename);
      return;
    }

    if (!data || !data.length) {
      console.warn('No data available to export');
      return;
    }

    const csvRows = this.buildCsvRows(data);
    const csvContent = csvRows.join('\r\n');
    this.downloadBlob(csvContent, filename);
  }

  private buildCsvRows<T extends object>(data: T[]): string[] {
    const headers = this.extractHeaders(data);
    const rows = data.map(row =>
      headers.map(field =>
        this.escapeCsv(this.getCellValue((row as Record<string, unknown>)[field]))
      ).join(',')
    );

    return [headers.join(','), ...rows];
  }

  private extractHeaders<T extends object>(data: T[]): string[] {
    return Object.keys(data[0]);
  }

  private getCellValue(value: unknown): unknown {
    if (typeof value === 'object' && value !== null && 'value' in value) {
      value = (value as { value: unknown }).value;
    }

    if (Array.isArray(value)) {
      return value.join('; ');
    }

    return value ?? '';
  }

  private escapeCsv(value: unknown): string {
    const str = String(value);
    const escaped = str.replace(/"/g, '""');
    return /[",\n\r]/.test(escaped) ? `"${escaped}"` : escaped;
  }

  private downloadBlob(content: string, filename: string): void {
    const bom = '\uFEFF'; // BOM for Excel compatibility
    const blob = new Blob([bom + content], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);

    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);

    window.URL.revokeObjectURL(url);
  }

  private formatNamePart(name: string): string {
    return name
      .replace(/[<>:"/\\|?*\x00-\x1F]/g, '')
      .trim()                               
      .replace(/\s+/g, '_');
  }

  private getTimestamp(): string {
    const now = new Date();
    const pad = (n: number) => n.toString().padStart(2, '0');

    const year = now.getFullYear();
    const month = pad(now.getMonth() + 1);
    const day = pad(now.getDate());
    const hours = pad(now.getHours());
    const minutes = pad(now.getMinutes());
    const seconds = pad(now.getSeconds());

    return `${year}-${month}-${day}_${hours}-${minutes}-${seconds}`;
  }

  getCsvFileName(name: string, context?: string): string {
    const safeName = this.formatNamePart(name);
    const timestamp = this.getTimestamp();

    return context
      ? `${safeName}_${context}_${timestamp}.csv`
      : `${safeName}_${timestamp}.csv`;
  }

}
